// src/components/dashboard/BulkActionBar.tsx
//
// Bulk operations used to work only on an explicit list of ids, and the table
// only ever had fifty rows on screen, so pausing a 50,000-email campaign meant
// paging through it a thousand times. The bar offers to act on everything
// matching the current filter instead, and shows the number it is about to
// touch before you commit to it.

import React from 'react';
import { Pause, Play, RefreshCw, Trash2, XCircle, Archive } from 'lucide-react';
import { BulkAction } from '../../services/dashboard';

interface BulkActionBarProps {
  /** How many rows are ticked on this page. */
  selectedCount: number;
  /** How many rows the current filter matches in total. */
  matchingCount: number;
  /** True once the operator has escalated from "this page" to "everything matching". */
  allMatchingSelected: boolean;
  onSelectAllMatching: () => void;
  onClearSelection: () => void;
  onAction: (action: BulkAction) => void;
  busy?: boolean;
}

const ACTIONS: {
  action: BulkAction;
  label: string;
  icon: React.ReactNode;
  className: string;
  title: string;
}[] = [
  {
    action: 'pause',
    label: 'Hold',
    icon: <Pause size={15} />,
    className: 'bg-amber-500 hover:bg-amber-600',
    title: 'Stop these being picked up, without losing them.',
  },
  {
    action: 'resume',
    label: 'Release',
    icon: <Play size={15} />,
    className: 'bg-green-600 hover:bg-green-700',
    title: 'Let held emails be picked up again.',
  },
  {
    action: 'retry',
    label: 'Retry',
    icon: <RefreshCw size={15} />,
    className: 'bg-blue-600 hover:bg-blue-700',
    title: 'Put failed emails back in the queue.',
  },
  {
    action: 'cancel',
    label: 'Cancel',
    icon: <XCircle size={15} />,
    className: 'bg-gray-700 hover:bg-gray-800',
    title: 'Withdraw these without sending. The record is kept.',
  },
  {
    action: 'archive',
    label: 'Archive',
    icon: <Archive size={15} />,
    className: 'bg-gray-600 hover:bg-gray-700',
    title: 'Move finished emails out of the working views.',
  },
  {
    action: 'delete',
    label: 'Delete',
    icon: <Trash2 size={15} />,
    className: 'bg-red-600 hover:bg-red-700',
    title: 'Remove the record entirely. Prefer Cancel unless you mean it.',
  },
];

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  matchingCount,
  allMatchingSelected,
  onSelectAllMatching,
  onClearSelection,
  onAction,
  busy = false,
}) => {
  if (selectedCount === 0 && !allMatchingSelected) {
    return null;
  }

  const targetCount = allMatchingSelected ? matchingCount : selectedCount;
  const canOfferAll = !allMatchingSelected && matchingCount > selectedCount;

  return (
    <div className="bg-gray-900 text-white rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-x-4 gap-y-3">
      <div className="text-sm">
        <span className="font-semibold tabular-nums">{targetCount.toLocaleString()}</span>{' '}
        {targetCount === 1 ? 'email' : 'emails'} selected
        {allMatchingSelected && (
          <span className="text-gray-400"> — everything matching this filter</span>
        )}
      </div>

      {canOfferAll && (
        <button
          onClick={onSelectAllMatching}
          className="text-sm font-medium text-blue-300 hover:text-blue-200 underline underline-offset-2"
        >
          Select all {matchingCount.toLocaleString()} matching
        </button>
      )}

      <div className="flex flex-wrap gap-2 ml-auto">
        {ACTIONS.map(({ action, label, icon, className, title }) => (
          <button
            key={action}
            onClick={() => onAction(action)}
            disabled={busy}
            title={title}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
          >
            {icon}
            {label}
          </button>
        ))}

        <button
          onClick={onClearSelection}
          disabled={busy}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-50"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default BulkActionBar;
